// frontend/src/pages/ContactPage/ContactPage.jsx

import React, { useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, IconButton, Avatar } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import MailOutlineIcon from "@mui/icons-material/MailOutline";
// import GitHubIcon from '@mui/icons-material/GitHub';
// import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Helmet } from 'react-helmet-async';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import './ContactPage.css';

// Animation variants for the grid and cards
const gridContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const gridItemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
  },
};

const ContactPage = () => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const emailAddress = "support@vetra-ai.com";
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);

    const handleScroll = (e) => {
        if (e.target.scrollTop > 20) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(emailAddress).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        }).catch(err => {
            console.error('Failed to copy email: ', err);
        });
    };
    
    // Particle background logic
    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: {
            events: { onHover: { enable: true, mode: 'repulse' }, resize: true },
            modes: { repulse: { distance: 100, duration: 0.4 } },
        },
        particles: {
            color: { value: '#ffffff' },
            links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 },
            move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 1, straight: false },
            number: { density: { enable: true, area: 800 }, value: 80 },
            opacity: { value: 0.5 },
            shape: { type: 'circle' },
            size: { value: { min: 1, max: 3 } },
        },
        detectRetina: true,
    };

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className={`cp-page-container ${isScrolled ? 'scrolled' : ''}`}
        >
            <Particles id="tsparticles-contact" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('contactPage.helmet.title')}</title>
                <meta name="description" content={t('contactPage.helmet.description')} />
            </Helmet>
            
            <div className="cp-nav-container">
                <UniversalNav />
            </div>

            <main className="cp-content-wrapper">
                <Box className="cp-glass-container">
                    <Box className="cp-header">
                        <Typography variant="h3" component="h1">
                            {t('contactPage.mainTitle')}
                        </Typography>
                        <Typography variant="subtitle1" component="h2">
                            {t('contactPage.subtitle')}
                        </Typography>
                    </Box>
                    
                    <motion.div
                        className="contact-grid"
                        variants={gridContainerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        <motion.div variants={gridItemVariants}>
                            <Paper elevation={3} className="contact-option-card primary">
                                <Avatar className="contact-icon"><ChatIcon /></Avatar>
                                <Typography variant="h6" component="h3">{t('contactPage.aiAssistant.title')}</Typography>
                                <Typography variant="body2" className="contact-text">
                                    {t('contactPage.aiAssistant.text')}
                                </Typography>
                                <Typography variant="caption" className="contact-card-cta" sx={{ mt: 'auto', pt: 1 }}>
                                    {t('contactPage.aiAssistant.cta')}
                                </Typography>
                            </Paper>
                        </motion.div>

                        <motion.div variants={gridItemVariants}>
                            <Paper elevation={2} className="contact-option-card">
                                <Avatar className="contact-icon"><MailOutlineIcon /></Avatar>
                                <Typography variant="h6" component="h3">{t('contactPage.emailSupport.title')}</Typography>
                                <Typography variant="body2" className="contact-text">
                                    {t('contactPage.emailSupport.text')}
                                </Typography>
                                <div className="email-display-container">
                                    <a href={`mailto:${emailAddress}`} className="contact-email-link">
                                        {emailAddress}
                                    </a>
                                    <IconButton 
                                        onClick={handleCopyEmail} 
                                        className="contact-copy-button" 
                                        size="small"
                                        aria-label={t('contactPage.aria.copyEmail')}
                                    >
                                        <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                </div>
                                {copied && <span className="copy-feedback-contact">{t('contactPage.copyFeedback')}</span>}
                            </Paper>
                        </motion.div>
                    </motion.div>
                    
                    {/* <motion.div variants={gridItemVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                        <Box className="cp-section social-section">
                            <Typography variant="h5" component="h2">
                                {t('contactPage.connect.title')}
                            </Typography>
                            <Typography paragraph>
                                {t('contactPage.connect.text')}
                            </Typography>
                            <Box className="social-links-container">
                                <Button startIcon={<GitHubIcon />} href="https://github.com/vetra-ai/vetra-ai/tree/main" target="_blank" rel="noopener noreferrer" variant="outlined" className="social-button">{t('contactPage.buttons.github')}</Button>
                                <Button startIcon={<LinkedInIcon />} href="https://www.linkedin.com/in/diego-bettega-59a649b7/" target="_blank" rel="noopener noreferrer" variant="outlined" className="social-button">{t('contactPage.buttons.linkedin')}</Button>
                            </Box>
                        </Box>
                    </motion.div> */}
                </Box>
            </main>
        </div>
    );
};

export default ContactPage;